import { store } from "../app/store";
import {
  type IBookingRoom,
  type IRoomCategory,
} from "../features/booking/bookingInterface";
import {
  type IHotelRoomCategory,
  type IHotelRoomCategoryAvailability,
  type IHotelRoomOccupancyAvailability,
} from "../features/hotel/hotelInterface";
import { normalizeRoomPrice } from "./mewsPrice";
import { combineVoucherRates } from "./rate";

/**
 * Get room availability by room ctegory id
 * @param adultCount
 * @param childCount
 * @param roomCategoryAvailability
 * @returns IRoomCategory | null
 */
export const getRoomCategoryAvailability = (
  adultCount: number | null,
  childCount: number | null,
  roomCategoryAvailability: IHotelRoomCategoryAvailability,
): IRoomCategory | null => {
  const availabilities = filterRoomOccupancyAvailabilitiesByOccupancy(
    adultCount,
    childCount,
    roomCategoryAvailability.RoomOccupancyAvailabilities,
  );

  if (availabilities.length) {
    const standardRoom = availabilities[0];
    const pricing = standardRoom.Pricing[0];
    return {
      RoomCategoryId: roomCategoryAvailability.RoomCategoryId,
      Pricing: pricing,
    };
  }
  return null;
};

/**
 * Get room availability with pricing
 * @param roomCategoryId
 * @param adultCount
 * @param childCount
 * @param roomCategoryAvailabilities
 * @returns IRoomCategory | null
 */
export const getRoomCategory = (
  roomCategoryId: string,
  adultCount: number | null,
  childCount: number | null,
): IRoomCategory | null => {
  // Find category to get availability and price
  const roomCategoryAvailability = findRoomCategoryAvailability(roomCategoryId);
  if (roomCategoryAvailability) {
    return getRoomCategoryAvailability(
      adultCount,
      childCount,
      roomCategoryAvailability,
    );
  } else {
    return null;
  }
};

/**
 * Find availability by category id
 * @param roomCategoryAvailabilities
 * @param roomCategoryId
 * @returns IHotelRoomCategoryAvailability | undefined
 */
export const findRoomCategoryAvailability = (
  roomCategoryId: string,
): IHotelRoomCategoryAvailability | undefined => {
  const { hotel } = store.getState();
  return hotel.RoomCategoryAvailabilities.find(
    (r) => r.RoomCategoryId === roomCategoryId,
  );
};

/**
 * Get availability bij occupancy
 * @param adultCount
 * @param childCount
 * @param roomOccupancyAvailabilities
 * @returns IHotelRoomOccupancyAvailability[]
 */
export const filterRoomOccupancyAvailabilitiesByOccupancy = (
  adultCount: number | null,
  childCount: number | null,
  roomOccupancyAvailabilities: IHotelRoomOccupancyAvailability[],
): IHotelRoomOccupancyAvailability[] => {
  return roomOccupancyAvailabilities.filter((occupany) => {
    // Check on adult and child
    if (adultCount !== null && childCount !== null) {
      return (
        occupany.AdultCount === adultCount && occupany.ChildCount === childCount
      );
    }

    // Check on adult only
    else if (adultCount !== null && childCount === null) {
      return occupany.AdultCount === adultCount;
    }

    // Check on child only
    else if (adultCount === null && childCount !== null) {
      return occupany.ChildCount === childCount;
    }

    return true;
  });
};

/**
 * Find availability by category id
 * @param roomCategoryId
 * @param bookedRooms
 * @returns number
 */
export const getRoomAvailability = ({
  Id,
  bookedRooms = 0,
}: {
  Id: IHotelRoomCategory["Id"];
  bookedRooms?: number;
}): number => {
  const { hotel } = store.getState();
  let count = 0;

  // Check if availibility exists for room category
  const category = hotel.RoomCategoryAvailabilities.find(
    (r) => r.RoomCategoryId === Id,
  );
  if (category) {
    count = category.AvailableRoomCount;

    // Minus already booked rooms from availability
    count = count - bookedRooms! > 0 ? count - bookedRooms! : 0;
  }

  return count;
};

export const getRoomRates = (
  roomCategoryId: string,
  bookedRooms: IBookingRoom[],
) => {
  const { hotel } = store.getState();
  const category = hotel.RoomCategoryAvailabilities.find(
    (r) => r.RoomCategoryId === roomCategoryId,
  );

  let ratePrice: {
    AvarageCount: number;
    RateId: string;
    Price: {
      AveragePerNight: Record<string, number>;
      Total: Record<string, number>;
    };
    MaxPrice: {
      AveragePerNight: Record<string, number>;
      Total: Record<string, number>;
    };
  }[] = [];
  hotel.Rates.forEach((rate) => {
    ratePrice.push({
      AvarageCount: 0,
      RateId: rate.Id,
      Price: {
        AveragePerNight: { [hotel.Culture.CurrencyCode]: 0 },
        Total: { [hotel.Culture.CurrencyCode]: 0 },
      },
      MaxPrice: {
        AveragePerNight: { [hotel.Culture.CurrencyCode]: 0 },
        Total: { [hotel.Culture.CurrencyCode]: 0 },
      },
    });
  });
  bookedRooms.forEach((room) => {
    hotel.Rates.forEach((rate) => {
      const roa =
        category?.RoomOccupancyAvailabilities.find(
          (occupany) =>
            occupany.AdultCount === room.AdultCount &&
            occupany.ChildCount === room.ChildCount,
        ) ??
        category?.RoomOccupancyAvailabilities.find((occupany) => {
          const persons = (occupany.OccupancyData ?? []).reduce(
            (sum, item) => sum + (item.PersonCount ?? 0),
            0,
          );
          return persons === room.AdultCount + room.ChildCount;
        });
      if (roa) {
        const pricing = roa.Pricing.find((p) => p.RateId === rate.Id);
        if (pricing) {
          const currencyCode = hotel.Culture.CurrencyCode;
          const price = normalizeRoomPrice(pricing.Price, currencyCode);
          const maxPrice = normalizeRoomPrice(pricing.MaxPrice, currencyCode);
          // Sum up the price for rate
          ratePrice.forEach((rp) => {
            if (rp.RateId === rate.Id) {
              rp.AvarageCount = rp.AvarageCount + 1;
              rp.Price.AveragePerNight[currencyCode] +=
                price.AveragePerNight[currencyCode] ?? 0;
              rp.Price.Total[currencyCode] +=
                price.Total[currencyCode] ?? 0;
              rp.MaxPrice.AveragePerNight[currencyCode] +=
                maxPrice.AveragePerNight[currencyCode] ?? 0;
              rp.MaxPrice.Total[currencyCode] +=
                maxPrice.Total[currencyCode] ?? 0;
            }
          });
        }
      }
    });
  });

  type AggregatedRatePrice = {
    AvarageCount: number;
    RateId: string;
    Price: {
      AveragePerNight: Record<string, number>;
      Total: Record<string, number>;
    };
    MaxPrice: {
      AveragePerNight: Record<string, number>;
      Total: Record<string, number>;
    };
    ReferencePrice?: {
      AveragePerNight: Record<string, number>;
      Total: Record<string, number>;
    };
  };

  const rates = ratePrice
    .filter((rp) => rp.AvarageCount > 0)
    .map(
      (rp): AggregatedRatePrice => ({
        AvarageCount: rp.AvarageCount,
        RateId: rp.RateId,
        Price: {
          AveragePerNight: {
            [hotel.Culture.CurrencyCode]:
              rp.Price.AveragePerNight[hotel.Culture.CurrencyCode],
          },
          Total: {
            [hotel.Culture.CurrencyCode]:
              rp.Price.Total[hotel.Culture.CurrencyCode],
          },
        },
        MaxPrice: {
          AveragePerNight: {
            [hotel.Culture.CurrencyCode]:
              rp.MaxPrice.AveragePerNight[hotel.Culture.CurrencyCode],
          },
          Total: {
            [hotel.Culture.CurrencyCode]:
              rp.MaxPrice.Total[hotel.Culture.CurrencyCode],
          },
        },
      }),
    )
    .map((rates: AggregatedRatePrice, _, allRates: AggregatedRatePrice[]) => {
      const currencyCode = hotel.Culture.CurrencyCode;
      let referenceRateId: string | null = null;
      let highestAvg = 0;

      allRates.forEach((rate: AggregatedRatePrice) => {
        const avg = rate.Price.AveragePerNight[currencyCode] ?? 0;
        if (avg > highestAvg) {
          highestAvg = avg;
          referenceRateId = rate.RateId;
        }
      });

      const referenceRate = allRates.find(
        (r: AggregatedRatePrice) => r.RateId === referenceRateId,
      );
      if (!referenceRate || referenceRateId === rates.RateId) {
        return rates;
      }

      const refAvg = referenceRate.Price.AveragePerNight[currencyCode] ?? 0;
      const currentAvg = rates.Price.AveragePerNight[currencyCode] ?? 0;
      if (refAvg <= currentAvg) {
        return rates;
      }

      return {
        ...rates,
        ReferencePrice: {
          AveragePerNight: referenceRate.Price.AveragePerNight,
          Total: referenceRate.Price.Total,
        },
      };
    });

  return combineVoucherRates(rates, hotel.Culture.CurrencyCode);
};
